import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPageUrl } from "@/utils";
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { ArrowLeft, FileText, CheckCircle2, ChevronsUpDown, Check, Download, Eye, Play, Upload, GitBranch, ExternalLink, BarChart3, TrendingUp, AlertCircle, ChevronDown, ChevronUp, Plus, Edit, Trash2, Snowflake, RefreshCw, Code, BookOpen, AppWindow } from 'lucide-react';
import { format } from 'date-fns';
import { runEndpoint } from '@/services/flow';
import { fetchData } from '@/fetchapi/fetchapi';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import ParquetViewer from '@/components/ParquetViewer';
import ExcelViewer from '@/components/ExcelViewer';
import CSVViewer from '@/components/CSVViewer';
import { downloadFile, getSignedUrl } from '@/utils/fileUtils';
import UploadFileWithDatePickerDialog from '@/components/datasource-actions/UploadFileWithDatePicker';
import { SourceFieldMappingView, ElementVersion, ReferenceValueView, DataElementView } from '@/entities/all';
import { exportDataElements } from '@/utils/excelTemplateGenerator';
import ACTSummaryCard from '@/components/act-scores/ACTSummaryCard';
import EditDataSourceModal from '@/components/datasources/EditDataSourceModal';
import DeleteDataSourceDialog from '@/components/datasources/DeleteDataSourceDialog';
import CreateElementFromFieldModal from '@/components/datasources/CreateElementFromFieldModal';
import EditMappingModal from '@/components/mappings/EditMappingModal';
import CreateMappingModal from '@/components/mappings/CreateMappingModal';
import DeleteMappingDialog from '@/components/mappings/DeleteMappingDialog';
import AddElementsFromFieldsDialog from '@/components/datasources/AddElementsFromFieldsDialog';
import NewElementModal from '@/components/elements/NewElementModal';
import ElementVersionModal from '@/components/elements/ElementVersionModal';
import ProcessingRulesPipeline from '@/components/elements/ProcessingRulesPipeline';
import MappingsTable from '@/components/mappings/MappingsTable';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataSource } from '@/entities/all';
import AccessPoliciesPanel from '@/components/policies/AccessPoliciesPanel';

function ElementHoverCell({ mapping, mappingCount, onElementClick, onMappingsClick, onRulesClick, onRefListClick }) {
  const [open, setOpen] = useState(false);
  const closeTimer = React.useRef(null);

  const handleOpen = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };
  const handleContentEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  const handleClick = (e) => {
    e.stopPropagation();
    setOpen(false);
    if (onElementClick) onElementClick(mapping);
  };

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <div
          onMouseEnter={handleOpen}
          onMouseLeave={handleClose}
          onClick={handleClick}
          className="text-sm font-medium truncate text-nav-active hover:text-nav-active-hover cursor-pointer"
        >
          {mapping.element_canonical_name || 'Unknown'}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-3 max-w-xs"
        onMouseEnter={handleContentEnter}
        onMouseLeave={handleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="space-y-2 text-sm">
          <div
            className="font-semibold text-blue-600 hover:text-blue-800 cursor-pointer hover:underline"
            onClick={(e) => handleClick(e)}
          >
            {mapping.element_canonical_name}
          </div>
          {mapping.element_data_type && (
            <div className="text-xs text-slate-500">Type: {mapping.element_data_type}</div>
          )}
          <div className="flex flex-col gap-1.5 pt-1 border-t">
            <div
              className="flex items-center gap-2 text-slate-600 hover:text-blue-600 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                if (onMappingsClick) onMappingsClick(mapping);
              }}
            >
              <GitBranch className="w-3.5 h-3.5" />
              <span>{mappingCount} mapping{mappingCount !== 1 ? 's' : ''}</span>
            </div>
            <div
              className="flex items-center gap-2 text-slate-600 hover:text-blue-600 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                if (onRulesClick) onRulesClick(mapping);
              }}
            >
              <Code className="w-3.5 h-3.5" />
              <span>{mapping.rule_count || 0} processing rule{mapping.rule_count !== 1 ? 's' : ''}</span>
            </div>
            <div
              className={`flex items-center gap-2 ${mapping.has_reference_list ? 'text-slate-600 hover:text-blue-600 cursor-pointer' : 'text-slate-400'}`}
              onClick={(e) => {
                if (mapping.has_reference_list) {
                  e.stopPropagation();
                  setOpen(false);
                  if (onRefListClick) onRefListClick(mapping);
                }
              }}
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>{mapping.has_reference_list ? mapping.reference_list_name : 'No reference list'}</span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RefListValuesContent({ listId }) {
  const [values, setValues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (listId) {
      ReferenceValueView.filter({ list_id: listId }, 'canonical_value')
        .then(setValues)
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [listId]);

  if (isLoading) {
    return <div className="py-8 text-center text-slate-500">Loading values...</div>;
  }

  if (values.length === 0) {
    return <div className="py-8 text-center text-slate-500">No values in this reference list.</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Canonical Value</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {values.map(v => (
          <TableRow key={v.id}>
            <TableCell className="font-medium">{v.canonical_value}</TableCell>
            <TableCell className="text-sm text-slate-600">{v.value_description || '-'}</TableCell>
            <TableCell>
              <Badge variant="outline" className={v.is_active ? 'text-green-700' : 'text-slate-400'}>
                {v.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function adjustTableName(name) {
  if (!name) return '';
  let validName = name.replace(/[^a-zA-Z0-9_]/g, '_');
  if (/^[0-9]/.test(validName)) {
    validName = 't_' + validName;
  }
  if (validName.length > 255) {
    validName = validName.substring(0, 255);
  }
  if (!validName) {
    validName = 'table';
  }
  return validName;
}

export default function DataSourceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState(null);
  const [dateEntries, setDateEntries] = useState([]);
  const [processedResults, setProcessedResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());
  const [viewingFile, setViewingFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingsExpanded, setMappingsExpanded] = useState(false);
  const [analysisExpanded, setAnalysisExpanded] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [createElementModalOpen, setCreateElementModalOpen] = useState(false);
  const [selectedUnmappedField, setSelectedUnmappedField] = useState(null);
  const [editingMapping, setEditingMapping] = useState(null);
  const [isEditMappingModalOpen, setIsEditMappingModalOpen] = useState(false);
  const [isCreateMappingModalOpen, setIsCreateMappingModalOpen] = useState(false);
  const [createMappingFieldName, setCreateMappingFieldName] = useState(null);
  const [isNewElementModalOpen, setIsNewElementModalOpen] = useState(false);
  const [viewingElement, setViewingElement] = useState(null); // { element, version }
  const [elementMappingsModal, setElementMappingsModal] = useState(null); // { name, mappings }
  const [elementRulesModal, setElementRulesModal] = useState(null); // { name, elementVersionId }
  const [refListModal, setRefListModal] = useState(null); // { name, listId }
  const [deleteMappingDialogOpen, setDeleteMappingDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [addElementsDialogOpen, setAddElementsDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const activeMappingModalRef = useRef(null);
  const [hasIcebergMetadata, setHasIcebergMetadata] = useState(false);
  const [icebergMetadataFile, setIcebergMetadataFile] = useState(null);
  const [icebergConflicts, setIcebergConflicts] = useState(null);
  const [icebergSyncing, setIcebergSyncing] = useState(false);
  // On-demand agent & app generation (auto-stamping was retired; the user
  // generates a source's app or agent from here when they want one).
  const [generatingApp, setGeneratingApp] = useState(false);
  const [generatingAgent, setGeneratingAgent] = useState(false);
  const [generatedViewName, setGeneratedViewName] = useState(null); // set once an app is generated
  const [generatedAgent, setGeneratedAgent] = useState(false);      // true once an agent is generated

  useEffect(() => {
    if (id) {
      loadDataSource();
      loadFiles();
      loadProcessedResults();
      loadMappings(true);
      checkActiveJob();
    }
  }, [id]);

  // Poll active job status
  useEffect(() => {
    if (!activeJobId) return;
    const interval = setInterval(async () => {
      try {
        const response = await fetchData(`governance_job?id=${activeJobId}`);
        // CRUD returns single object when id is provided, array wrapper otherwise
        const job = response?.data ? response.data[0] : (Array.isArray(response) ? response[0] : response);
        if (job && (job.status === 'completed' || job.status === 'failed')) {
          setIsProcessing(false);
          setActiveJobId(null);
          if (job.status === 'completed') {
            toast.success('Processing completed!');
            loadProcessedResults();
            loadDataSource();
            loadMappings(true);
          } else {
            toast.error(`Processing failed: ${job.error_message || 'Unknown error'}`);
          }
        }
      } catch (err) {
        console.error('Error polling job status:', err);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [activeJobId]);

  const checkActiveJob = async () => {
    try {
      const response = await fetchData(`governance_job?data_source_id=${id}&sort=-created_at&limit=1`);
      const rows = response?.data || (Array.isArray(response) ? response : []);
      const latestJob = rows[0];
      if (latestJob && (latestJob.status === 'accepted' || latestJob.status === 'processing')) {
        setIsProcessing(true);
        setActiveJobId(latestJob.id);
      }
    } catch (err) {
      // Ignore — not critical
    }
  };

  const loadDataSource = async () => {
    setIsLoading(true);
    try {
      const data = await fetchData(`data_source?id=${id}`);
      setDataSource(data);
    } catch (error) {
      console.error('Error loading data source:', error);
      toast.error('Failed to load data source');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFiles = async () => {
    setFilesLoading(true);
    try {
      const response = await runEndpoint('cortex.vdd.list_datasource_files', {
        dataSourceId: id
      });

      // Handle new response shape: { files: [{ datePath, files, fileCount, totalSize, latestModified }], hasIcebergMetadata, icebergMetadataFile }
      const rawDateEntries = response?.files || response || [];
      const hasIceberg = response?.hasIcebergMetadata || false;
      const icebergFile = response?.icebergMetadataFile || null;

      setHasIcebergMetadata(hasIceberg);
      setIcebergMetadataFile(icebergFile);

      // Enrich files within each date entry
      const entries = rawDateEntries.map(entry => ({
        ...entry,
        files: (entry.files || []).map(file => ({
          ...file,
          fileName: file.key ? file.key.split('/').pop() : ''
        }))
      }));

      setDateEntries(entries);

      // Auto-select latest (first) date if available
      if (entries.length > 0 && !selectedDate) {
        setSelectedDate(entries[0]);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      toast.error('Failed to load files');
    } finally {
      setFilesLoading(false);
    }
  };

  const loadProcessedResults = async () => {
    setResultsLoading(true);
    try {
      const response = await runEndpoint('cortex.list_processed_results', {
        dataSourceId: id
      });
      setProcessedResults(response || []);
    } catch (error) {
      console.error('Error loading processed results:', error);
      toast.error('Failed to load processed results');
    } finally {
      setResultsLoading(false);
    }
  };

  const loadMappings = async (showLoading = false) => {
    if (showLoading) setMappingsLoading(true);
    try {
      // Use view with server-side filtering by source_id
      const mappingsData = await SourceFieldMappingView.filter({ source_id: id }, 'source_field_name');

      console.log('Mappings for source:', mappingsData);
      setMappings(mappingsData);
    } catch (error) {
      console.error('Error loading mappings:', error);
      toast.error('Failed to load mappings');
    } finally {
      if (showLoading) setMappingsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800 border-green-200';
      case 'Deprecated': return 'bg-red-100 text-red-800 border-red-200';
      case 'Maintenance': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    const map = {
      'API': 'bg-blue-100 text-blue-800',
      'Database': 'bg-purple-100 text-purple-800',
      'File Feed': 'bg-orange-100 text-orange-800',
      'Stream': 'bg-teal-100 text-teal-800',
      'Manual': 'bg-gray-100 text-gray-800',
    };
    return map[type] || map['Manual'];
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const handleDownloadFile = async (fileKey, fileName, bucket) => {
    const downloadKey = `${fileKey}`;
    setDownloadingFiles(prev => new Set(prev).add(downloadKey));

    try {
      await downloadFile(fileKey, fileName, bucket);
    } catch (error) {
      // Error already handled in utility
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(downloadKey);
        return newSet;
      });
    }
  };

  const handleViewFile = async (fileKey, fileName, bucket) => {
    try {
      // Instead of passing URL, pass the fileKey directly
      // ParquetViewer will handle fetching via utility
      setViewingFile({ key: fileKey, name: fileName, bucket });
    } catch (error) {
      console.error('Error preparing file for viewing:', error);
      toast.error('Failed to load file for viewing');
    }
  };

  const handleCloseViewer = () => {
    setViewingFile(null);
  };

  const handleUploadDialogChange = (open) => {
    setUploadDialogOpen(open);
    // Reload files when dialog closes (if upload was successful)
    if (!open) {
      loadFiles();
    }
  };

  const handleGenerateAnalysis = async () => {
    const files = selectedDate?.files;
    if (!files || files.length === 0) return;

    setIsProcessing(true);
    try {
      for (const file of files) {
        await runEndpoint('cortex.datasource.action.generate_analysis_statistics', {
          file,
          dataSourceId: parseInt(id)
        });
      }

      toast.success('Analysis statistics generated successfully!');
      await loadDataSource();
    } catch (error) {
      console.error('Analysis generation error:', error);
      toast.error('Analysis generation failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateMappings = async () => {
    setMappingsLoading(true);
    try {
      const response = await runEndpoint('cortex.vdd.load.data_source_mappings', {
        dataSourceId: parseInt(id)
      });

      if (response) {
        toast.success('Source field mappings created successfully!');

        // Wait a moment for the backend to finish, then reload once
        setTimeout(async () => {
          try {
            // Use view with server-side filtering
            const mappingsData = await SourceFieldMappingView.filter({ source_id: id }, 'source_field_name');
            setMappings(mappingsData);
          } catch (error) {
            console.error('Error reloading mappings:', error);
          } finally {
            setMappingsLoading(false);
          }
        }, 2000); // Wait 2 seconds for backend to complete
      } else {
        toast.error('Source field mappings creation failed. Please try again.');
        setMappingsLoading(false);
      }
    } catch (error) {
      console.error('Source field mappings creation error:', error);
      toast.error('Source field mappings creation failed. Please try again.');
      setMappingsLoading(false);
    }
  };

  // Generate the FULL default app (explorer view + backing agent + registry
  // row) for this source on demand.
  const handleGenerateApp = async () => {
    setGeneratingApp(true);
    try {
      const res = await fetchData(`data_sources/${id}/generate_app`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      if (res && res.view_name) {
        setGeneratedViewName(res.view_name);
        setGeneratedAgent(true); // generate_app also stamps the agent
        toast.success('App generated. Open it below or from the Apps rail.');
      } else {
        toast.error('App generation failed. Please try again.');
      }
    } catch (e) {
      toast.error(`App generation failed: ${e.message || e}`);
    } finally {
      setGeneratingApp(false);
    }
  };

  // Generate ONLY the backing agent (no explorer view, no registry row).
  const handleGenerateAgent = async () => {
    setGeneratingAgent(true);
    try {
      const res = await fetchData(`data_sources/${id}/generate_agent`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      if (res && res.agent_name) {
        setGeneratedAgent(true);
        toast.success('Agent generated. It is available in the orchestrator chat.');
      } else {
        toast.error('Agent generation failed. Please try again.');
      }
    } catch (e) {
      toast.error(`Agent generation failed: ${e.message || e}`);
    } finally {
      setGeneratingAgent(false);
    }
  };

  const handleRunProcessing = async () => {
    const files = selectedDate?.files;
    if (!files || files.length === 0) return;

    setIsProcessing(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');

      const response = await fetchData('governance/process', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dataSourceId: parseInt(id),
          sourceFiles: files
        })
      });

      if (response && response.jobId) {
        toast.success(`Processing started! Job ${response.jobId} accepted.`);
        setActiveJobId(response.jobId);
        // isProcessing stays true — polling will clear it on completion
      } else {
        toast.error('Processing failed. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Processing error:', error);
      setIsProcessing(false);
      if (error?.code === 'ICEBERG_METADATA_CONFLICT') {
        const conflictMessages = (error.conflicts || [])
          .filter(c => c.severity === 'error')
          .map(c => c.message)
          .join('; ');
        toast.error(
          `Processing blocked: Iceberg schema conflicts detected. ${conflictMessages || 'Sync Iceberg metadata or update field mappings to resolve.'}`,
          { duration: 10000 }
        );
        await checkIcebergConflicts();
      } else {
        const msg = error?.message || 'Processing failed. Please try again.';
        toast.error(msg, { duration: 6000 });
      }
    }
  };

  const checkIcebergConflicts = async () => {
    try {
      const result = await runEndpoint('cortex.vdd.compare_iceberg_with_existing', {
        dataSourceId: parseInt(id)
      });
      setIcebergConflicts(result || null);
    } catch (error) {
      console.error('Error checking Iceberg conflicts:', error);
    }
  };

  const handleFixTypeMismatch = async (conflict) => {
    if (!conflict.elementVersionId || !conflict.suggestedType) return;
    try {
      await ElementVersion.update(conflict.elementVersionId, {
        data_type: conflict.suggestedType
      });
      toast.success(`Updated "${conflict.elementName}" type to "${conflict.suggestedType}"`);
      await checkIcebergConflicts();
    } catch (error) {
      console.error('Error fixing type mismatch:', error);
      toast.error(`Failed to update type for "${conflict.elementName}"`);
    }
  };

  const handleFixAllTypeMismatches = async () => {
    const typeMismatches = (icebergConflicts?.conflicts || [])
      .filter(c => c.type === 'type_mismatch' && c.elementVersionId && c.suggestedType);
    if (typeMismatches.length === 0) return;

    try {
      for (const conflict of typeMismatches) {
        await ElementVersion.update(conflict.elementVersionId, {
          data_type: conflict.suggestedType
        });
      }
      toast.success(`Updated ${typeMismatches.length} element type(s) to match Iceberg schema`);
      await checkIcebergConflicts();
    } catch (error) {
      console.error('Error fixing type mismatches:', error);
      toast.error('Failed to update some element types');
    }
  };

  const handleSyncFromIceberg = async () => {
    if (!icebergMetadataFile) return;

    setIcebergSyncing(true);
    try {
      const response = await runEndpoint('cortex.vdd.parse_iceberg_to_analysis', {
        dataSourceId: parseInt(id),
        sourceFile: icebergMetadataFile
      });

      if (response) {
        toast.success('Iceberg metadata synced successfully!');
        await loadDataSource();
        await checkIcebergConflicts();
      } else {
        toast.error('Iceberg sync failed. Please try again.');
      }
    } catch (error) {
      console.error('Iceberg sync error:', error);
      toast.error('Iceberg sync failed. Please try again.');
    } finally {
      setIcebergSyncing(false);
    }
  };

  // Check Iceberg conflicts when data source loads with iceberg metadata
  useEffect(() => {
    if (dataSource?.meta?.iceberg) {
      checkIcebergConflicts();
    }
  }, [dataSource?.meta?.iceberg]);

  const isParquetFile = (fileName) => {
    return fileName && fileName.toLowerCase().endsWith('.parquet');
  };

  const isExcelFile = (fileName) => {
    if (!fileName) return false;
    const lower = fileName.toLowerCase();
    return lower.endsWith('.xls') || lower.endsWith('.xlsx');
  };

  const isCSVFile = (fileName) => {
    return fileName && fileName.toLowerCase().endsWith('.csv');
  };

  const isViewableFile = (fileName) => {
    return isParquetFile(fileName) || isExcelFile(fileName) || isCSVFile(fileName);
  };

  const getResultType = (key) => {
    if (key.includes('invalidDataElements')) return 'Invalid Values';
    if (key.includes('validDataElements')) return 'Validated Values';
    if (key.includes('sourceFields')) return 'Values with no processing logic';
    if (key.includes('/raw_values/')) return 'Raw Values (Change Tracking)';
    if (key.includes('/change_tracker/')) return 'Change Tracker';
    if (key.includes('/actScores/')) return 'ACT Scores';
    if (key.includes('/processed_output/')) return 'Processed Output';
    return 'Processed File';
  };

  // Multi-sheet xlsx sources produce one parquet per non-empty sheet. The
  // engine writes filenames as `{dateLabel}-{sheetName}-{resultType}.parquet`
  // where dateLabel is `year=YYYY-month=MM-day=DD`. Extract the sheet token
  // so the UI can distinguish e.g. Webscrape_411 vs Sheet1 results in the
  // Processed Results list — otherwise everything just says "Change Tracker".
  const getSheetName = (key) => {
    if (!key) return '';
    const parts = String(key).split('/');
    const fileName = parts[parts.length - 1];
    // Strip "year=YYYY-month=MM-day=DD-" prefix and the
    // "-{resultType}.parquet" suffix. Whatever is between is the sheet.
    const m = fileName.match(
      /^year=\d{4}-month=\d{2}-day=\d{2}-(.+)-(change_tracker|validDataElements|invalidDataElements|sourceFields|actScores|processed_output|raw_values)\.parquet$/
    );
    return m ? m[1] : '';
  };

  const getResultTypeColor = (key) => {
    if (key.includes('invalidDataElements')) return 'bg-red-50 text-red-700 border-red-200';
    if (key.includes('validDataElements')) return 'bg-green-50 text-green-700 border-green-200';
    if (key.includes('sourceFields')) return 'bg-slate-50 text-slate-600 border-slate-200';
    if (key.includes('/raw_values/')) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (key.includes('/change_tracker/')) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (key.includes('/actScores/')) return 'bg-purple-50 text-purple-700 border-purple-200';
    if (key.includes('/processed_output/')) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-slate-50 text-slate-600 border-slate-200';
  };

  const getElementName = (mapping) => {
    // View provides element_canonical_name directly
    return mapping.element_canonical_name || 'Unknown';
  };

  // Filter date entries for combobox based on search
  const filteredDateEntries = useMemo(() => {
    if (!search) return dateEntries;
    const query = search.toLowerCase();
    return dateEntries.filter(entry =>
      entry.datePath?.toLowerCase().includes(query) ||
      entry.files?.some(f => f.fileName?.toLowerCase().includes(query))
    );
  }, [dateEntries, search]);

  // Extract source file name from file key (without extension)
  const getSourceFileName = (fileKey) => {
    if (!fileKey) return '';
    const parts = fileKey.split('/');
    const fileName = parts[parts.length - 1];
    const nameWithoutExt = fileName.split('.').slice(0, -1).join('.');
    return nameWithoutExt;
  };

  // Filter processed results based on selected date
  const filteredProcessedResults = useMemo(() => {
    if (!selectedDate) return processedResults;

    const selectedDatePath = selectedDate.datePath;

    return processedResults.filter(result => {
      const resultDatePath = result.datePath || '';

      // Must match the same date path as the selected date
      if (resultDatePath !== selectedDatePath) {
        return false;
      }

      return true;
    });
  }, [processedResults, selectedDate]);

  // Group processed results by date
  const groupedResults = useMemo(() => {
    const groups = {};
    filteredProcessedResults.forEach(result => {
      const date = result.datePath || 'Unknown';
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(result);
    });
    return groups;
  }, [filteredProcessedResults]);

  const unmappedFields = useMemo(() => {
    if (!dataSource?.analysis || mappings.length === 0) return [];
    // Guarded parse — a malformed analysis string must not white-screen the page.
    let analysis = dataSource.analysis;
    if (typeof analysis === 'string') { try { analysis = JSON.parse(analysis); } catch { return []; } }
    if (!Array.isArray(analysis)) return [];
    const mappedFieldNames = new Set(mappings.map(m => m.source_field_name));
    return analysis.filter(f => !mappedFieldNames.has(f.field_name));
  }, [dataSource?.analysis, mappings]);


  if (isLoading) {
    return (
      <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full rounded-[8px]" />
        <Skeleton className="h-48 w-full rounded-[8px]" />
      </div>
    );
  }

  if (!dataSource) {
    return (
      <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-2">
        <p className="text-sm" style={{ color: 'var(--page-muted)' }}>Data source not found</p>
        <button
          onClick={() => navigate(createPageUrl('data-sources'))}
          className="flex items-center gap-1 w-fit text-sm text-nav-active cursor-pointer bg-transparent border-0 p-0 hover:text-nav-active-hover transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Data Sources
        </button>
      </div>
    );
  }

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Back button */}
      <button
        onClick={() => navigate(createPageUrl('data-sources'))}
        className="flex items-center gap-1 w-fit text-sm text-nav-active cursor-pointer bg-transparent border-0 p-0 -mb-2 hover:text-nav-active-hover transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Data Sources
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl md:text-[32px] font-medium leading-tight text-page-title break-words">{dataSource.source_name}</h1>
            {dataSource.is_golden_source && (
              <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap"
                style={{ background: 'rgba(233,171,48,0.18)', color: 'rgba(180,120,0,0.85)' }}>
                Golden
              </span>
            )}
            {hasIcebergMetadata && (
              <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap"
                style={{ background: 'rgba(127,199,249,0.20)', color: '#377AD6' }}>
                <Snowflake size={10} />
                Iceberg Table
              </span>
            )}
          </div>
          {dataSource.description && (
            <p className="text-sm font-normal text-page-muted">{dataSource.description}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <button
            onClick={() => setIsDeleteDialogOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-red-400 text-red-500 text-sm font-medium cursor-pointer bg-transparent"
          >
            <Trash2 size={20} strokeWidth={2.5} />
            Delete
          </button>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active text-sm font-medium cursor-pointer bg-card-bg hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <Edit size={20} strokeWidth={2.5} />
            Edit
          </button>
        </div>
      </div>

      {/* Agent & app — generated ON DEMAND (rag sources have their own
          Knowledge Base explorer/agent, so hide these there). */}
      {String(dataSource.kind) !== 'rag' && (
        <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <AppWindow size={18} color="#377AD6" />
              <div className="min-w-0">
                <h2 className="text-base font-medium" style={{ color: 'var(--page-text)' }}>Agent &amp; app</h2>
                <p className="text-sm" style={{ color: 'var(--page-muted)' }}>
                  Generate an explorer app or a chat agent for this data source when you want one.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {generatedViewName ? (
                <button
                  onClick={() => navigate(createPageUrl(`apps/${generatedViewName}`))}
                  className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer border-0 hover:bg-nav-active-dark transition-colors"
                >
                  <ExternalLink size={18} strokeWidth={2.5} />
                  Open app
                </button>
              ) : (
                <button
                  onClick={handleGenerateApp}
                  disabled={generatingApp}
                  className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active text-sm font-medium cursor-pointer bg-card-bg disabled:opacity-50 disabled:cursor-not-allowed hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
                >
                  {generatingApp ? <RefreshCw size={18} strokeWidth={2.5} className="animate-spin" /> : <AppWindow size={18} strokeWidth={2.5} />}
                  {generatingApp ? 'Generating…' : 'Generate app'}
                </button>
              )}
              {generatedAgent ? (
                <span className="flex items-center gap-2 h-9 px-4 rounded-[5px] text-sm font-medium"
                  style={{ background: 'rgba(76,175,80,0.14)', color: 'rgba(46,125,50,0.9)' }}>
                  <CheckCircle2 size={18} strokeWidth={2.5} />
                  Agent available in chat
                </span>
              ) : (
                <button
                  onClick={handleGenerateAgent}
                  disabled={generatingAgent}
                  className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active text-sm font-medium cursor-pointer bg-card-bg disabled:opacity-50 disabled:cursor-not-allowed hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
                >
                  {generatingAgent ? <RefreshCw size={18} strokeWidth={2.5} className="animate-spin" /> : <BookOpen size={18} strokeWidth={2.5} />}
                  {generatingAgent ? 'Generating…' : 'Generate agent'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Data Source File Selection */}
      <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={18} color="#377AD6" />
            <h2 className="text-base font-medium" style={{ color: 'var(--page-text)' }}>Select Data Source File</h2>
          </div>
          <button
            onClick={() => setUploadDialogOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer border-0 hover:bg-nav-active-dark transition-colors"
          >
            <Upload size={20} strokeWidth={2.5} />
            Upload File
          </button>
        </div>
        {filesLoading ? (
          <Skeleton className="h-10 w-full rounded-[5px]" />
        ) : dateEntries.length === 0 ? (
          <p className="text-sm text-center py-4" style={{ color: 'var(--page-muted)' }}>No files uploaded yet</p>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-end">
            <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
              <PopoverTrigger asChild>
                <button
                  role="combobox"
                  aria-expanded={comboboxOpen}
                  className="flex items-center justify-between gap-2 w-full sm:w-auto sm:min-w-[320px] sm:max-w-full h-9 px-3 rounded-[5px] border-2 border-nav-active bg-card-bg text-sm font-medium text-nav-active cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
                >
                  <span className="truncate">
                    {selectedDate ? `${selectedDate.datePath} (${selectedDate.fileCount} file${selectedDate.fileCount !== 1 ? 's' : ''})` : 'Select date...'}
                  </span>
                  <ChevronsUpDown size={16} strokeWidth={2.5} className="shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[min(600px,calc(100vw-2rem))] p-0">
                <Command>
                  <CommandInput
                    placeholder="Search dates..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <CommandList>
                    {filteredDateEntries.length === 0 ? (
                      <CommandEmpty>No dates found.</CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {filteredDateEntries.map((entry) => (
                          <CommandItem
                            key={entry.datePath}
                            onClick={() => {
                              setSelectedDate(entry);
                              setComboboxOpen(false);
                              setSearch('');
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedDate?.datePath === entry.datePath ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex-1">
                              <div className="font-medium">{entry.datePath}</div>
                              <div className="text-xs text-slate-500">
                                {entry.fileCount} file{entry.fileCount !== 1 ? 's' : ''} • {formatFileSize(entry.totalSize)}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            </div>

            {selectedDate && (
              <div className="mt-3 p-4 rounded-[8px]" style={{ background: 'var(--table-stripe)' }}>
                <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                  <div className="text-sm font-medium min-w-0 break-all" style={{ color: 'var(--page-text)' }}>{selectedDate.datePath}</div>
                  <button
                    onClick={handleRunProcessing}
                    disabled={isProcessing || dateEntries.length === 0 || mappings.length === 0 || icebergConflicts?.severity === 'error'}
                    className="flex items-center gap-2 h-8 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-nav-active-dark transition-colors"
                    title={
                      dateEntries.length === 0
                        ? 'Upload a source file first to run processing'
                        : mappings.length === 0
                        ? 'Create source field mappings first to run processing'
                        : icebergConflicts?.severity === 'error'
                        ? 'Resolve Iceberg metadata conflicts first'
                        : ''
                    }
                  >
                    <Play size={16} strokeWidth={2.5} />
                    {isProcessing ? 'Processing...' : 'Run Processing'}
                  </button>
                </div>

                {(dateEntries.length === 0 || mappings.length === 0) && (
                  <div className="mb-3 p-3 rounded-[8px] flex items-start gap-2" style={{ background: 'rgba(233,171,48,0.12)', border: '1px solid rgba(233,171,48,0.3)' }}>
                    <AlertCircle size={16} style={{ color: 'rgba(233,171,48,0.9)', flexShrink: 0, marginTop: 1 }} />
                    <div className="text-sm" style={{ color: 'rgba(180,120,0,0.85)' }}>
                      <span className="font-medium">Processing disabled:</span>
                      {dateEntries.length === 0 && ' Upload at least one source file first.'}
                      {dateEntries.length > 0 && mappings.length === 0 && ' Create source field mappings first to enable data validation and processing.'}
                    </div>
                  </div>
                )}

                {icebergConflicts?.severity === 'error' && (() => {
                  const typeMismatches = icebergConflicts.conflicts.filter(c => c.type === 'type_mismatch' && c.severity === 'error');
                  const otherErrors = icebergConflicts.conflicts.filter(c => c.severity === 'error' && c.type !== 'type_mismatch');
                  return (
                    <div className="mb-3 p-3 rounded-[8px] space-y-3" style={{ background: 'rgba(222,111,107,0.08)', border: '1px solid rgba(222,111,107,0.3)' }}>
                      <div className="flex items-start gap-2">
                        <AlertCircle size={16} style={{ color: 'rgba(222,111,107,0.9)', flexShrink: 0, marginTop: 1 }} />
                        <span className="text-sm font-medium" style={{ color: 'rgba(180,60,57,0.9)' }}>Processing blocked: Iceberg schema conflicts detected.</span>
                      </div>
                      {otherErrors.length > 0 && (
                        <ul className="text-sm list-disc list-inside ml-6" style={{ color: 'rgba(180,60,57,0.9)' }}>
                          {otherErrors.map((c, i) => <li key={i}>{c.message}</li>)}
                        </ul>
                      )}
                      {typeMismatches.length > 0 && (
                        <div className="ml-6">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium" style={{ color: 'rgba(180,60,57,0.9)' }}>
                              Type mismatches ({typeMismatches.length}):
                            </span>
                            {typeMismatches.length > 1 && typeMismatches.every(c => c.elementVersionId && c.suggestedType) && (
                              <button
                                className="h-7 px-2 text-xs rounded-[4px] cursor-pointer border"
                                style={{ borderColor: 'rgba(222,111,107,0.5)', color: 'rgba(180,60,57,0.9)', background: 'transparent' }}
                                onClick={handleFixAllTypeMismatches}
                              >
                                Fix all types
                              </button>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            {typeMismatches.map((c, i) => (
                              <div key={i} className="flex flex-wrap items-center justify-between gap-2 text-sm rounded px-2 py-1.5"
                                style={{ background: 'rgba(222,111,107,0.10)', color: 'rgba(180,60,57,0.9)' }}>
                                <span className="min-w-0 break-words">
                                  <span className="font-medium">{c.fieldName}</span>
                                  {c.elementName && <span style={{ color: 'rgba(222,111,107,0.7)' }}> ({c.elementName})</span>}
                                  {': '}
                                  <span className="inline-flex items-center h-[16px] rounded px-1.5 text-xs border" style={{ borderColor: 'rgba(222,111,107,0.5)' }}>{c.elementType}</span>
                                  <span className="mx-1">→</span>
                                  <span className="inline-flex items-center h-[16px] rounded px-1.5 text-xs border" style={{ borderColor: 'rgba(77,179,0,0.5)', color: 'rgba(77,179,0,0.9)' }}>{c.suggestedType || c.icebergType}</span>
                                </span>
                                {c.elementVersionId && c.suggestedType && (
                                  <button
                                    className="h-6 px-2 text-xs rounded-[4px] flex-shrink-0 cursor-pointer border"
                                    style={{ borderColor: 'rgba(222,111,107,0.5)', color: 'rgba(180,60,57,0.9)', background: 'transparent' }}
                                    onClick={() => handleFixTypeMismatch(c)}
                                  >
                                    Fix
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {otherErrors.length > 0 && (
                        <p className="text-xs ml-6" style={{ color: 'rgba(180,60,57,0.7)' }}>
                          To resolve other conflicts: re-sync Iceberg metadata from the Field Analysis section below, or update the affected field mappings.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {icebergConflicts?.severity === 'warning' && (
                  <div className="mb-3 p-3 rounded-[8px] flex items-start gap-2" style={{ background: 'rgba(233,171,48,0.12)', border: '1px solid rgba(233,171,48,0.3)' }}>
                    <AlertCircle size={16} style={{ color: 'rgba(233,171,48,0.9)', flexShrink: 0, marginTop: 1 }} />
                    <div className="text-sm" style={{ color: 'rgba(180,120,0,0.85)' }}>
                      <span className="font-medium">Iceberg metadata warnings:</span>
                      <ul className="mt-1 list-disc list-inside">
                        {icebergConflicts.conflicts.map((c, i) => <li key={i}>{c.message}</li>)}
                      </ul>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <div className="mb-1" style={{ color: 'var(--page-muted)' }}>Files</div>
                    <div className="font-medium" style={{ color: 'var(--page-text)' }}>{selectedDate.fileCount}</div>
                  </div>
                  <div>
                    <div className="mb-1" style={{ color: 'var(--page-muted)' }}>Total Size</div>
                    <div className="font-medium" style={{ color: 'var(--page-text)' }}>{formatFileSize(selectedDate.totalSize)}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  {selectedDate.files.map((file, idx) => (
                    <div key={file.key || idx} className="flex items-center justify-between p-3 bg-card-bg rounded-[5px] gap-2" style={{ border: '1px solid var(--card-border)' }}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate" style={{ color: 'var(--page-text)' }}>{file.fileName}</div>
                        <div className="text-xs truncate" style={{ color: 'var(--page-muted)' }}>
                          {formatFileSize(file.size)}
                          {file.lastModified && new Date(file.lastModified).getTime()
                            ? ` • ${format(new Date(file.lastModified), 'MMM d, yyyy HH:mm')}`
                            : ''}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {isViewableFile(file.fileName) && (
                          <button
                            className="flex items-center justify-center w-8 h-8 rounded-[5px] cursor-pointer border-0 bg-transparent text-nav-hover hover:text-nav-active transition-colors"
                            onClick={() => handleViewFile(file.key, file.fileName, file.bucket)}
                            title="View file"
                          >
                            <Eye size={20} strokeWidth={2.5} />
                          </button>
                        )}
                        <button
                          className="flex items-center justify-center w-8 h-8 rounded-[5px] cursor-pointer border-0 bg-transparent text-nav-hover hover:text-nav-active transition-colors disabled:opacity-40"
                          onClick={() => handleDownloadFile(file.key, file.fileName, file.bucket)}
                          disabled={downloadingFiles.has(file.key)}
                          title="Download file"
                        >
                          <Download size={20} strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Processed Results */}
      <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
        <div className="flex items-center gap-2 mb-4 min-w-0">
          <CheckCircle2 size={18} color="#377AD6" className="shrink-0" />
          <h2 className="text-base font-medium min-w-0 break-words" style={{ color: 'var(--page-text)' }}>
            Processed Results
            {selectedDate && (
              <span className="font-normal text-sm ml-2" style={{ color: 'var(--page-muted)' }}>
                for {selectedDate.datePath}
              </span>
            )}
          </h2>
        </div>
        {resultsLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-[5px]" />)}
          </div>
        ) : !selectedDate ? (
          <p className="text-sm text-center py-8" style={{ color: 'var(--page-muted)' }}>Select a date to view processed results</p>
        ) : filteredProcessedResults.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: 'var(--page-muted)' }}>No processed results for this file yet</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedResults).map(([date, results]) => (
              <div key={date}>
                <div className="text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--page-muted)' }}>
                  <div className="h-px flex-1" style={{ background: 'var(--card-border)' }} />
                  <span>{date}</span>
                  <div className="h-px flex-1" style={{ background: 'var(--card-border)' }} />
                </div>
                <div className="space-y-2">
                  {results.map((result, index) => (
                    <div
                      key={result.key || index}
                      className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-[5px]"
                      style={{ background: 'var(--table-stripe)', border: '1px solid var(--card-border)' }}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={16} color="#7AC5FF" className="shrink-0" />
                        <div className="text-sm font-medium min-w-0 break-words" style={{ color: 'var(--page-text)' }}>
                          {getResultType(result.key)}
                          {getSheetName(result.key) && (
                            <span className="ml-2 font-normal text-xs" style={{ color: 'var(--page-muted)' }}>
                              · {getSheetName(result.key)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4 ml-auto">
                        <div className="text-right text-sm" style={{ color: 'var(--page-muted)' }}>
                          <div>{formatFileSize(result.size)}</div>
                          {result.lastModified && new Date(result.lastModified).getTime() && (
                            <div className="text-xs">{format(new Date(result.lastModified), 'MMM d, yyyy HH:mm')}</div>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {isViewableFile(result.label) && (
                            <button
                              className="flex items-center justify-center w-8 h-8 rounded-[5px] cursor-pointer border-0 bg-transparent text-nav-hover hover:text-nav-active transition-colors"
                              onClick={() => handleViewFile(result.key, result.label, result.bucket)}
                              title="View file"
                            >
                              <Eye size={20} strokeWidth={2.5} />
                            </button>
                          )}
                          <button
                            className="flex items-center justify-center w-8 h-8 rounded-[5px] cursor-pointer border-0 bg-transparent text-nav-hover hover:text-nav-active transition-colors disabled:opacity-40"
                            onClick={() => handleDownloadFile(result.key, result.label, result.bucket)}
                            disabled={downloadingFiles.has(result.key)}
                            title="Download file"
                          >
                            <Download size={20} strokeWidth={2.5} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ACT Quality Scores Summary */}
      {selectedDate && filteredProcessedResults.length > 0 && (
        <ACTSummaryCard
          dataSourceId={id}
          datePath={selectedDate.datePath}
          selectedFile={selectedDate.files?.[0] || null}
        />
      )}

      {/* Source Field Mappings */}
      <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <GitBranch size={18} color="#377AD6" className="shrink-0" />
            <h2 className="text-base font-medium min-w-0 break-words" style={{ color: 'var(--page-text)' }}>
              Source Field Mappings
              {mappings.length > 0 && (
                <span className="font-normal text-sm ml-1" style={{ color: 'var(--page-muted)' }}>({mappings.length})</span>
              )}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {mappings.length > 0 && (
              <button
                className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={async () => {
                  setIsExporting(true);
                  try {
                    const elementIds = [...new Set(mappings.map(m => m.canonical_element_id).filter(Boolean))];
                    const allElements = await DataElementView.list('canonical_name');
                    const filtered = allElements.filter(el => elementIds.includes(el.id));
                    exportDataElements(filtered);
                  } catch (error) {
                    console.error('Error exporting elements:', error);
                    toast.error('Failed to export elements');
                  } finally {
                    setIsExporting(false);
                  }
                }}
                disabled={isExporting}
              >
                <Download size={16} strokeWidth={2.5} />
                {isExporting ? 'Exporting...' : 'Export'}
              </button>
            )}
            {mappings.length > 0 && (
              <button
                className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleCreateMappings}
                disabled={!dataSource?.analysis || mappingsLoading}
                title={!dataSource?.analysis ? 'Run analysis first to regenerate field mappings' : 'Regenerate mappings from latest analysis'}
              >
                <RefreshCw size={16} strokeWidth={2.5} className={mappingsLoading ? 'animate-spin' : ''} />
                {mappingsLoading ? 'Regenerating...' : 'Regenerate Mappings'}
              </button>
            )}
            {mappings.length > 0 && (
              <button
                className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] text-sm font-medium cursor-pointer bg-transparent border-0 text-nav-active hover:text-nav-active-hover transition-colors"
                onClick={() => setMappingsExpanded(!mappingsExpanded)}
              >
                {mappingsExpanded ? <><ChevronUp size={16} strokeWidth={2.5} />Collapse</> : <><ChevronDown size={16} strokeWidth={2.5} />Expand</>}
              </button>
            )}
          </div>
        </div>

        {mappings.length === 0 ? (
          <div className="flex items-start gap-3 p-4 rounded-[8px]" style={{ background: 'rgba(233,171,48,0.12)', border: '1px solid rgba(233,171,48,0.3)' }}>
            <AlertCircle size={18} style={{ color: 'rgba(233,171,48,0.9)', flexShrink: 0, marginTop: 1 }} />
            <div className="flex-1">
              <div className="font-medium mb-1" style={{ color: 'rgba(180,120,0,0.9)' }}>No Source Field Mappings</div>
              <p className="text-sm mb-3" style={{ color: 'rgba(180,120,0,0.85)' }}>
                Source field mappings link source data fields to canonical data elements for validation and transformation.
                {!dataSource?.analysis && ' Run analysis on a source file first to generate field mappings.'}
                {dataSource?.analysis && !mappingsLoading && ' Generate mappings automatically based on the analysis results.'}
                {mappingsLoading && ' Creating mappings from analysis results...'}
              </p>
              <button
                className="flex items-center gap-2 h-8 px-3 rounded-[5px] text-white text-sm font-medium cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'rgba(180,120,0,0.85)' }}
                onClick={handleCreateMappings}
                disabled={!dataSource?.analysis || mappingsLoading}
                title={!dataSource?.analysis ? 'Run analysis first to generate field mappings' : ''}
              >
                <GitBranch size={16} strokeWidth={2.5} />
                {mappingsLoading ? 'Creating...' : !dataSource?.analysis ? 'Run Analysis First' : 'Create Mappings'}
              </button>
            </div>
          </div>
        ) : mappingsExpanded && (
          <>
            {mappingsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-11 w-full" />)}
              </div>
            ) : (
              <>
                {/* Mappings flex table */}
                <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
                <div className="flex flex-col min-w-[520px]">
                  <div className="flex items-center h-9 px-5 gap-[10px] border border-table-header"
                    style={{ background: 'var(--table-header-bg)', borderRadius: '8px 8px 0 0' }}>
                    <div className="flex-1 min-w-0 text-sm" style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Source Field</div>
                    <div className="flex-1 min-w-0 text-sm" style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Data Element</div>
                    <div className="w-[110px] shrink-0 text-sm" style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Confidence</div>
                    <div className="w-[50px] shrink-0" />
                  </div>
                  {mappings.map((mapping, i) => {
                    const elementMappingCount = mappings.filter(m => m.canonical_element_id === mapping.canonical_element_id).length;
                    const confidence = mapping.confidence_score || mapping.mapping_confidence || 0;
                    const confColor = confidence >= 0.9 ? '#4DB300' : confidence >= 0.7 ? 'rgba(233,171,48,0.9)' : 'rgba(222,111,107,0.9)';
                    const confBg = confidence >= 0.9 ? 'rgba(209,238,176,0.50)' : confidence >= 0.7 ? 'rgba(233,171,48,0.20)' : 'rgba(222,111,107,0.20)';
                    const rowBg = i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)';
                    return (
                      <div
                        key={mapping.id}
                        className="group flex items-center h-11 px-5 gap-[10px] cursor-pointer"
                        style={{ background: rowBg }}
                        onClick={() => { setEditingMapping(mapping); setIsEditMappingModalOpen(true); }}
                      >
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium truncate block text-page-text group-hover:text-nav-active transition-colors">
                            {mapping.source_field_name}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <ElementHoverCell mapping={mapping} mappingCount={elementMappingCount} onElementClick={async (m) => {
                            const versions = await ElementVersion.filter({ element_id: m.canonical_element_id }, '-version_number', 1);
                            setViewingElement({
                              element: { id: m.canonical_element_id, canonical_name: m.element_canonical_name, current_version_number: m.element_current_version },
                              version: versions[0] || null
                            });
                          }} onMappingsClick={(m) => {
                            const elMappings = mappings.filter(mp => mp.canonical_element_id === m.canonical_element_id);
                            setElementMappingsModal({ name: m.element_canonical_name, mappings: elMappings, elementId: m.canonical_element_id });
                          }} onRefListClick={(m) => {
                            setRefListModal({ name: m.reference_list_name, listId: m.reference_list_id });
                          }} onRulesClick={async (m) => {
                            const versions = await ElementVersion.filter({ element_id: m.canonical_element_id }, '-version_number', 1);
                            if (versions.length > 0) {
                              setElementRulesModal({ name: m.element_canonical_name, elementVersionId: versions[0].id, elementId: m.canonical_element_id });
                            }
                          }} />
                        </div>
                        <div className="w-[110px] shrink-0">
                          <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap"
                            style={{ background: confBg, color: confColor }}>
                            {Math.round(confidence * 100)}%
                          </span>
                        </div>
                        <div className="w-[50px] shrink-0 flex justify-end" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="flex items-center justify-center bg-transparent border-0 cursor-pointer text-nav-hover hover:text-nav-active transition-colors"
                            onClick={() => { setMappingToDelete(mapping); setDeleteMappingDialogOpen(true); }}
                          >
                            <Trash2 size={20} strokeWidth={2.5} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>

                {unmappedFields.length > 0 && (
                  <div className="mt-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <AlertCircle size={16} style={{ color: 'rgba(233,171,48,0.9)' }} className="shrink-0" />
                        <span className="font-medium text-sm" style={{ color: 'rgba(180,120,0,0.9)' }}>
                          {unmappedFields.length} unmapped field{unmappedFields.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <button
                        className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
                        onClick={() => setAddElementsDialogOpen(true)}
                      >
                        <Plus size={16} strokeWidth={2.5} />
                        Add Elements
                      </button>
                    </div>
                    <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
                    <div className="flex flex-col rounded-[8px] overflow-hidden min-w-[660px]" style={{ border: '1px solid rgba(233,171,48,0.3)' }}>
                      <div className="flex items-center h-9 px-5 gap-[10px]"
                        style={{ background: 'rgba(233,171,48,0.15)', borderRadius: '8px 8px 0 0' }}>
                        <div className="flex-1 min-w-0 text-sm font-medium" style={{ color: 'rgba(180,120,0,0.9)' }}>Source Field</div>
                        <div className="flex-1 min-w-0 text-sm font-medium" style={{ color: 'rgba(180,120,0,0.9)' }}>Data Element</div>
                        <div className="w-[80px] shrink-0 text-sm font-medium" style={{ color: 'rgba(180,120,0,0.9)' }}>Rules</div>
                        <div className="w-[100px] shrink-0 text-sm font-medium" style={{ color: 'rgba(180,120,0,0.9)' }}>Ref List</div>
                        <div className="w-[100px] shrink-0 text-sm font-medium" style={{ color: 'rgba(180,120,0,0.9)' }}>Confidence</div>
                        <div className="flex-1 min-w-0 text-sm font-medium" style={{ color: 'rgba(180,120,0,0.9)' }}>Notes</div>
                      </div>
                      {unmappedFields.map((field, i) => (
                        <div
                          key={field.field_name}
                          className="group flex items-center h-11 px-5 gap-[10px] cursor-pointer"
                          style={{ background: i % 2 === 0 ? 'rgba(233,171,48,0.05)' : 'var(--card-bg)' }}
                          onClick={() => { setCreateMappingFieldName(field.field_name); setIsCreateMappingModalOpen(true); }}
                        >
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-[rgba(180,120,0,0.9)] group-hover:text-nav-active transition-colors">{field.field_name}</span>
                          </div>
                          <div className="flex-1 min-w-0"><span style={{ color: 'rgba(233,171,48,0.6)' }}>—</span></div>
                          <div className="w-[80px] shrink-0"><span style={{ color: 'rgba(233,171,48,0.6)' }}>-</span></div>
                          <div className="w-[100px] shrink-0"><span style={{ color: 'rgba(233,171,48,0.6)' }}>-</span></div>
                          <div className="w-[100px] shrink-0"><span style={{ color: 'rgba(233,171,48,0.6)' }}>-</span></div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm italic" style={{ color: 'rgba(180,120,0,0.7)' }}>Click to create mapping</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Analysis Statistics */}
      {dataSource && (() => {
        if (!dataSource.analysis) {
          if (hasIcebergMetadata) {
            return (
              <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 size={18} color="#377AD6" className="shrink-0" />
                  <h2 className="text-base font-medium" style={{ color: 'var(--page-text)' }}>Field Analysis Statistics</h2>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-[8px]" style={{ background: 'rgba(127,199,249,0.15)', border: '1px solid rgba(55,122,214,0.3)' }}>
                  <Snowflake size={18} style={{ color: '#377AD6', flexShrink: 0, marginTop: 1 }} />
                  <div className="flex-1">
                    <div className="font-medium mb-1" style={{ color: 'var(--page-text)' }}>Iceberg Metadata Available</div>
                    <p className="text-sm mb-3" style={{ color: 'var(--page-muted)' }}>
                      This data source has Iceberg metadata that contains schema definitions, column statistics, and snapshot history.
                      Sync from Iceberg metadata to automatically populate analysis statistics.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="flex items-center gap-2 h-8 px-3 rounded-[5px] text-white text-sm font-medium cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed bg-nav-active hover:bg-nav-active-dark transition-colors"
                        disabled={icebergSyncing}
                        onClick={handleSyncFromIceberg}
                      >
                        <RefreshCw size={16} strokeWidth={2.5} className={cn(icebergSyncing && "animate-spin")} />
                        {icebergSyncing ? 'Syncing...' : 'Sync from Iceberg Metadata'}
                      </button>
                      <button
                        className="flex items-center gap-2 h-8 px-3 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={dateEntries.length === 0 || !selectedDate || isProcessing}
                        onClick={handleGenerateAnalysis}
                      >
                        <BarChart3 size={16} strokeWidth={2.5} />
                        Generate from File Instead
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 size={18} color="#377AD6" className="shrink-0" />
                <h2 className="text-base font-medium" style={{ color: 'var(--page-text)' }}>Field Analysis Statistics</h2>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-[8px]" style={{ background: 'rgba(233,171,48,0.12)', border: '1px solid rgba(233,171,48,0.3)' }}>
                <AlertCircle size={18} style={{ color: 'rgba(233,171,48,0.9)', flexShrink: 0, marginTop: 1 }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium mb-1" style={{ color: 'rgba(180,120,0,0.9)' }}>No Analysis Statistics Available</div>
                  <p className="text-sm mb-3" style={{ color: 'rgba(180,120,0,0.85)' }}>
                    Field analysis provides insights into data quality, including null counts, distinct values, and common patterns.
                    {dateEntries.length === 0 && ' Upload a source file first, then generate analysis statistics.'}
                    {dateEntries.length > 0 && ' Generate analysis statistics to understand your data structure and quality.'}
                  </p>
                  <button
                    className="flex items-center gap-2 h-8 px-3 rounded-[5px] text-white text-sm font-medium cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'rgba(180,120,0,0.85)' }}
                    disabled={dateEntries.length === 0 || !selectedDate || isProcessing}
                    onClick={handleGenerateAnalysis}
                    title={dateEntries.length === 0 ? 'Upload a source file first to generate analysis' : !selectedDate ? 'Select a date first' : ''}
                  >
                    <BarChart3 size={16} strokeWidth={2.5} />
                    {isProcessing ? 'Generating...' : dateEntries.length === 0 ? 'Upload a file first' : !selectedDate ? 'Select a date first' : 'Generate Analysis Statistics'}
                  </button>
                </div>
              </div>
            </div>
          );
        }

        let analysisData = dataSource.analysis;
        if (typeof analysisData === 'string') {
          // Guarded — a malformed analysis string must not crash the render.
          try { analysisData = JSON.parse(analysisData); } catch { analysisData = null; }
        }

        if (Array.isArray(analysisData)) {
          return (
            <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2 min-w-0">
                  <BarChart3 size={18} color="#377AD6" className="shrink-0" />
                  <h2 className="text-base font-medium min-w-0 break-words" style={{ color: 'var(--page-text)' }}>
                    Field Analysis Statistics
                    <span className="font-normal text-sm ml-1" style={{ color: 'var(--page-muted)' }}>({analysisData.length} fields)</span>
                  </h2>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={hasIcebergMetadata ? icebergSyncing : (dateEntries.length === 0 || !selectedDate || isProcessing)}
                    onClick={hasIcebergMetadata ? handleSyncFromIceberg : handleGenerateAnalysis}
                  >
                    <RefreshCw size={16} strokeWidth={2.5} className={cn((icebergSyncing || isProcessing) && "animate-spin")} />
                    {(icebergSyncing || isProcessing) ? 'Regenerating...' : 'Regenerate'}
                  </button>
                  <button
                    className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] text-sm font-medium cursor-pointer bg-transparent border-0 text-nav-active hover:text-nav-active-hover transition-colors"
                    onClick={() => setAnalysisExpanded(!analysisExpanded)}
                  >
                    {analysisExpanded ? <><ChevronUp size={16} strokeWidth={2.5} />Collapse</> : <><ChevronDown size={16} strokeWidth={2.5} />Expand</>}
                  </button>
                </div>
              </div>
              {analysisExpanded && (
                <div className="space-y-4">
                  {analysisData.map((field, idx) => (
                    <div key={idx} className="p-4 rounded-[8px]" style={{ background: 'var(--table-stripe)', border: '1px solid var(--card-border)' }}>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <div className="font-semibold text-lg break-all min-w-0" style={{ color: 'var(--page-text)' }}>{field.field_name}</div>
                        {field.source === 'iceberg_metadata' && (
                          <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap"
                            style={{ background: 'rgba(127,199,249,0.20)', color: '#377AD6' }}>
                            <Snowflake size={10} />
                            From Iceberg
                          </span>
                        )}
                        {field.data_type && (
                          <span className="inline-flex items-center h-[16px] rounded px-2 text-xs border"
                            style={{ borderColor: 'var(--input-border)', color: 'var(--page-muted)', background: 'var(--card-bg)' }}>
                            {field.data_type}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div style={{ color: 'var(--page-muted)' }}>Total Count</div>
                          <div className="font-medium" style={{ color: 'var(--page-text)' }}>{field.count?.toLocaleString()}</div>
                        </div>
                        {field.distinct_count !== null && field.distinct_count !== undefined && (
                          <div>
                            <div style={{ color: 'var(--page-muted)' }}>Distinct Values</div>
                            <div className="font-medium" style={{ color: 'var(--page-text)' }}>{field.distinct_count?.toLocaleString()}</div>
                          </div>
                        )}
                        <div>
                          <div style={{ color: 'var(--page-muted)' }}>Null Count</div>
                          <div className="font-medium" style={{ color: 'var(--page-text)' }}>{field.null_count?.toLocaleString()}</div>
                        </div>
                        <div>
                          <div style={{ color: 'var(--page-muted)' }}>Null %</div>
                          <div className="font-medium" style={{ color: 'var(--page-text)' }}>{field.null_percentage?.toFixed(2)}%</div>
                        </div>
                        {field.lower_bound !== undefined && field.lower_bound !== null && (
                          <div className="min-w-0">
                            <div style={{ color: 'var(--page-muted)' }}>Min Value</div>
                            <div className="font-medium truncate" style={{ color: 'var(--page-text)' }} title={String(field.lower_bound)}>{String(field.lower_bound)}</div>
                          </div>
                        )}
                        {field.upper_bound !== undefined && field.upper_bound !== null && (
                          <div className="min-w-0">
                            <div style={{ color: 'var(--page-muted)' }}>Max Value</div>
                            <div className="font-medium truncate" style={{ color: 'var(--page-text)' }} title={String(field.upper_bound)}>{String(field.upper_bound)}</div>
                          </div>
                        )}
                        {field.avg_string_length !== undefined && (
                          <>
                            <div>
                              <div style={{ color: 'var(--page-muted)' }}>Avg Length</div>
                              <div className="font-medium" style={{ color: 'var(--page-text)' }}>{field.avg_string_length?.toFixed(1)}</div>
                            </div>
                            <div>
                              <div style={{ color: 'var(--page-muted)' }}>Min Length</div>
                              <div className="font-medium" style={{ color: 'var(--page-text)' }}>{field.min_string_length}</div>
                            </div>
                            <div>
                              <div style={{ color: 'var(--page-muted)' }}>Max Length</div>
                              <div className="font-medium" style={{ color: 'var(--page-text)' }}>{field.max_string_length}</div>
                            </div>
                          </>
                        )}
                      </div>
                      {field.most_common && field.most_common.length > 0 && (
                        <div className="mt-3 pt-3 flex flex-wrap gap-2" style={{ borderTop: '1px solid var(--card-border)' }}>
                          <div className="text-sm w-full mb-1" style={{ color: 'var(--page-muted)' }}>Most Common Values:</div>
                          {field.most_common.slice(0, 5).map((item, i) => (
                            <span key={i} className="px-2 py-1 rounded text-xs" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', color: 'var(--page-text)' }}>
                              {item.value} ({item.count})
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }

        return (
          <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2 min-w-0">
                <BarChart3 size={18} color="#377AD6" className="shrink-0" />
                <h2 className="text-base font-medium" style={{ color: 'var(--page-text)' }}>Analysis Statistics</h2>
              </div>
              <button
                className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] text-sm font-medium cursor-pointer bg-transparent border-0 text-nav-active hover:text-nav-active-hover transition-colors"
                onClick={() => setAnalysisExpanded(!analysisExpanded)}
              >
                {analysisExpanded ? <><ChevronUp size={16} strokeWidth={2.5} />Collapse</> : <><ChevronDown size={16} strokeWidth={2.5} />Expand</>}
              </button>
            </div>
            {analysisExpanded && (
              <div className="p-4 rounded-[8px] overflow-x-auto" style={{ background: 'var(--table-stripe)' }}>
                <pre className="whitespace-pre-wrap break-all text-sm font-mono" style={{ color: 'var(--page-text)' }}>
                  {JSON.stringify(analysisData, null, 2)}
                </pre>
              </div>
            )}
          </div>
        );
      })()}

      {/* File Viewer Dialogs */}
      {viewingFile && isParquetFile(viewingFile.name) && (
        <ParquetViewer
          open={!!viewingFile}
          onOpenChange={handleCloseViewer}
          fileKey={viewingFile.key}
          fileName={viewingFile.name}
          bucket={viewingFile.bucket}
        />
      )}

      {viewingFile && isExcelFile(viewingFile.name) && (
        <ExcelViewer
          open={!!viewingFile}
          onOpenChange={handleCloseViewer}
          fileKey={viewingFile.key}
          fileName={viewingFile.name}
          bucket={viewingFile.bucket}
        />
      )}

      {viewingFile && isCSVFile(viewingFile.name) && (
        <CSVViewer
          open={!!viewingFile}
          onOpenChange={handleCloseViewer}
          fileKey={viewingFile.key}
          fileName={viewingFile.name}
          bucket={viewingFile.bucket}
        />
      )}

      {/* Upload Dialog */}
      <UploadFileWithDatePickerDialog
        open={uploadDialogOpen}
        onOpenChange={handleUploadDialogChange}
        dataSourceId={id}
        sourceName={dataSource?.source_name}
      />

      {/* Edit Data Source Modal */}
      <EditDataSourceModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => {
          setIsEditModalOpen(false);
          loadDataSource();
        }}
        source={dataSource}
      />

      {/* Delete Data Source Dialog */}
      <DeleteDataSourceDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onSuccess={() => {
          setIsDeleteDialogOpen(false);
          navigate(createPageUrl('data-sources'));
        }}
        source={dataSource}
      />

      {/* Create Element from Unmapped Field Modal */}
      <CreateElementFromFieldModal
        isOpen={createElementModalOpen}
        onClose={() => {
          setCreateElementModalOpen(false);
          setSelectedUnmappedField(null);
        }}
        onSuccess={() => {
          setCreateElementModalOpen(false);
          setSelectedUnmappedField(null);
          loadMappings();
        }}
        field={selectedUnmappedField}
        dataSourceId={id}
      />

      {/* Edit Field Mapping Modal */}
      {isEditMappingModalOpen && <EditMappingModal
        ref={activeMappingModalRef}
        isOpen={isEditMappingModalOpen}
        onClose={() => {
          setIsEditMappingModalOpen(false);
          setEditingMapping(null);
        }}
        onMappingUpdated={(updatedFields) => {
          setIsEditMappingModalOpen(false);
          if (updatedFields) {
            setMappings(prev => prev.map(m => m.id === editingMapping?.id ? { ...m, ...updatedFields } : m));
          }
          setEditingMapping(null);
        }}
        mapping={editingMapping}
        onRequestNewElement={() => setIsNewElementModalOpen(true)}
      />}

      {/* Create Field Mapping Modal */}
      {isCreateMappingModalOpen && <CreateMappingModal
        ref={activeMappingModalRef}
        isOpen={isCreateMappingModalOpen}
        onClose={() => {
          setIsCreateMappingModalOpen(false);
          setCreateMappingFieldName(null);
        }}
        onMappingCreated={() => {
          setIsCreateMappingModalOpen(false);
          setCreateMappingFieldName(null);
          loadMappings();
        }}
        preselectedSourceId={id}
        preselectedFieldName={createMappingFieldName}
        onRequestNewElement={() => setIsNewElementModalOpen(true)}
      />}

      {/* New Element Modal (rendered at top level to avoid nested Dialog performance issues) */}
      {isNewElementModalOpen && <NewElementModal
        isOpen={isNewElementModalOpen}
        onClose={() => setIsNewElementModalOpen(false)}
        onElementCreated={async (newElement) => {
          setIsNewElementModalOpen(false);
          if (activeMappingModalRef.current?.refreshAndSelectElement) {
            await activeMappingModalRef.current.refreshAndSelectElement(newElement);
          }
        }}
      />}

      {elementMappingsModal && (
        <Dialog open={!!elementMappingsModal} onOpenChange={() => setElementMappingsModal(null)}>
          <DialogContent className="!max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between pr-8">
                <DialogTitle>Source Mappings: {elementMappingsModal.name}</DialogTitle>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
                  window.open(`/element-details?id=${elementMappingsModal.elementId}`, '_blank');
                }}>
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open Element
                </Button>
              </div>
            </DialogHeader>
            <MappingsTable
              mappings={elementMappingsModal.mappings}
              isLoading={false}
              onMappingUpdate={() => {
                setElementMappingsModal(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {elementRulesModal && (
        <Dialog open={!!elementRulesModal} onOpenChange={() => setElementRulesModal(null)}>
          <DialogContent className="!max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between pr-8">
                <DialogTitle>Processing Rules: {elementRulesModal.name}</DialogTitle>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
                  window.open(`/element-details?id=${elementRulesModal.elementId}&tab=processing`, '_blank');
                }}>
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open Element
                </Button>
              </div>
            </DialogHeader>
            <ProcessingRulesPipeline
              elementVersionId={elementRulesModal.elementVersionId}
              disabled={true}
            />
          </DialogContent>
        </Dialog>
      )}

      {refListModal && (
        <Dialog open={!!refListModal} onOpenChange={() => setRefListModal(null)}>
          <DialogContent className="!max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between pr-8">
                <DialogTitle>Reference List: {refListModal.name}</DialogTitle>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
                  window.open(`/reference-list-values?id=${refListModal.listId}`, '_blank');
                }}>
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open Reference List
                </Button>
              </div>
            </DialogHeader>
            <RefListValuesContent listId={refListModal.listId} />
          </DialogContent>
        </Dialog>
      )}

      {/* Data access policies (row/column entitlements) scoped to this source */}
      <AccessPoliciesPanel dataSourceId={id} />

      {viewingElement && <ElementVersionModal
        isOpen={!!viewingElement}
        onClose={() => setViewingElement(null)}
        element={viewingElement.element}
        version={viewingElement.version}
        onVersionSaved={() => {}}
        readOnly
        externalUrl={`/element-details?id=${viewingElement.element?.id}`}
      />}

      <DeleteMappingDialog
        isOpen={deleteMappingDialogOpen}
        onClose={() => {
          setDeleteMappingDialogOpen(false);
          setMappingToDelete(null);
        }}
        onSuccess={() => {
          setDeleteMappingDialogOpen(false);
          setMappings(prev => prev.filter(m => m.id !== mappingToDelete?.id));
          setMappingToDelete(null);
        }}
        mapping={mappingToDelete}
      />

      <AddElementsFromFieldsDialog
        open={addElementsDialogOpen}
        onOpenChange={setAddElementsDialogOpen}
        unmappedFields={unmappedFields}
        dataSourceId={id}
        onComplete={() => loadMappings(true)}
      />
    </div>
  );
}
